import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ControlDock } from "./components/ControlDock";
import { CurrentReadout } from "./components/CurrentReadout";
import { HistorySheet, type SheetStage } from "./components/HistorySheet";
import { MobileDock } from "./components/MobileDock";
import { VisualizationCanvas, type VisualizationHandle } from "./components/VisualizationCanvas";
import { useMediaQuery } from "./hooks/useMediaQuery";
import { useTermSound } from "./hooks/useTermSound";
import { useWorldStream } from "./hooks/useWorldStream";
import { formatDecimal, formatUtcTime, operationSymbol } from "./lib/format";
import { formatTermRate } from "./lib/timing";
import type { RecamanVisualizationSnapshot, VisualizationViewMode } from "./visualization";
import "./styles/app.css";

const UI_STATUS_DURATION_MS = 4_000;

export default function App() {
  const stream = useWorldStream();
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const tickMs = stream.world.snapshot.tickMs;
  const { enabled: soundEnabled, toggle: toggleSound } = useTermSound(
    stream.displayTerm,
    tickMs,
  );
  const visualizationRef = useRef<VisualizationHandle>(null);
  const [viewMode, setViewMode] = useState<VisualizationViewMode>("follow");
  const [sheetStage, setSheetStage] = useState<SheetStage>(0);
  const [narrationEnabled, setNarrationEnabled] = useState(false);
  const [narrationRate, setNarrationRate] = useState(10);
  const [announcerText, setAnnouncerText] = useState("");
  const [uiStatus, setUiStatus] = useState("");
  const lastAnnouncedRef = useRef<string | null>(null);

  const setTemporaryStatus = useCallback((message: string) => setUiStatus(message), []);

  useEffect(() => {
    if (!uiStatus) return;
    const timeout = window.setTimeout(() => setUiStatus(""), UI_STATUS_DURATION_MS);
    return () => window.clearTimeout(timeout);
  }, [uiStatus]);

  useEffect(() => {
    if (!narrationEnabled || stream.isPaused) return;
    const n = BigInt(stream.displayTerm.n);
    if (n % BigInt(narrationRate) !== 0n || lastAnnouncedRef.current === stream.displayTerm.n) return;
    lastAnnouncedRef.current = stream.displayTerm.n;
    const rule = stream.displayTerm.operation === "add" ? "加算" : "減算";
    setAnnouncerText(
      `第${formatDecimal(stream.displayTerm.n)}項、値${formatDecimal(stream.displayTerm.value)}、${formatDecimal(stream.displayTerm.step)}を${rule}。`,
    );
  }, [narrationEnabled, narrationRate, stream.displayTerm, stream.isPaused]);

  const onVisualizationViewChange = useCallback((snapshot: RecamanVisualizationSnapshot) => {
    setViewMode(snapshot.mode);
  }, []);

  const returnToLive = useCallback(() => {
    stream.returnToLive();
    visualizationRef.current?.follow();
    setViewMode("follow");
    setTemporaryStatus("世界の現在位置へ戻りました。");
  }, [setTemporaryStatus, stream]);

  const togglePause = useCallback(() => {
    if (stream.isPaused) {
      returnToLive();
      return;
    }
    stream.pauseDisplay();
    setTemporaryStatus("表示を一時停止しました。世界の数列は進行中です。");
  }, [returnToLive, setTemporaryStatus, stream]);

  const toggleExplore = useCallback(() => {
    if (viewMode === "explore") {
      returnToLive();
      return;
    }
    setViewMode("explore");
    setTemporaryStatus("探索モード。世界の数列は進行中です。");
  }, [returnToLive, setTemporaryStatus, viewMode]);

  const showOverview = useCallback(() => {
    visualizationRef.current?.overview();
    setViewMode("overview");
    setTemporaryStatus("現在の履歴を全景表示しています。");
  }, [setTemporaryStatus]);

  const shareCurrent = useCallback(async () => {
    const text = `RECAMÁN ∞ — n=${stream.displayTerm.n}, a(n)=${stream.displayTerm.value}`;
    const shareData = { title: "RECAMÁN ∞", text, url: window.location.origin };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        setTemporaryStatus("現在の項を共有しました。");
      } else {
        await navigator.clipboard.writeText(`${text} ${window.location.origin}`);
        setTemporaryStatus("現在の項をクリップボードへコピーしました。");
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      setTemporaryStatus("共有できませんでした。ブラウザの権限を確認してください。");
    }
  }, [setTemporaryStatus, stream.displayTerm.n, stream.displayTerm.value]);

  const toggleNarration = useCallback(() => {
    setNarrationEnabled((current) => {
      const next = !current;
      setTemporaryStatus(next ? `${narrationRate}項ごとの読み上げを開始しました。` : "項の読み上げを停止しました。");
      return next;
    });
  }, [narrationRate, setTemporaryStatus]);

  const visualTerms = stream.displayTerms;
  const isLiveView = !stream.isPaused && viewMode === "follow" && stream.behindBy === 0n;
  const syncTime = useMemo(
    () => formatUtcTime(stream.world.lastSyncMs ?? stream.world.snapshot.serverTimeMs),
    [stream.world.lastSyncMs, stream.world.snapshot.serverTimeMs],
  );
  const moveSymbol = operationSymbol(stream.displayTerm.operation);
  const termRate = formatTermRate(tickMs);

  return (
    <div className="observatory">
      <a className="skip-link" href="#current-term">現在の項へ移動</a>

      <header className="brand-lockup">
        <h1>RECAMÁN ∞</h1>
        <p>WORLD {stream.world.snapshot.worldId.padStart(4, "0")}</p>
      </header>

      <main>
        <figure className="visualization-figure" aria-labelledby="visual-caption">
          <VisualizationCanvas
            ref={visualizationRef}
            terms={visualTerms}
            tickMs={tickMs}
            mode={viewMode}
            reducedMotion={reducedMotion}
            historyIdentity={`${stream.world.snapshot.worldId}:${stream.world.snapshot.startedAtMs}`}
            historyThrough={stream.world.snapshot.currentN}
            onViewChange={onVisualizationViewChange}
          />
          <figcaption id="visual-caption" className="sr-only">
            レカマン数列を半円弧で表した図。加算はセンターラインの上を黄緑、減算は下をコーラルで通ります。現在値と履歴は本文でも確認できます。
          </figcaption>
        </figure>

        <CurrentReadout
          term={stream.displayTerm}
          connection={stream.world.connection}
          catchingUp={stream.world.snapshot.catchingUp}
          isPaused={stream.isPaused}
          isExploring={viewMode !== "follow"}
          behindBy={stream.behindBy}
        />
        <p className="sr-only">
          前の値 {formatDecimal(stream.displayTerm.previous)}。{formatDecimal(stream.displayTerm.step)}を
          {stream.displayTerm.operation === "add" ? "加算" : "減算"}。
        </p>

        <ControlDock
          isPaused={stream.isPaused}
          isLive={isLiveView}
          isExploring={viewMode === "explore" || viewMode === "overview"}
          soundEnabled={soundEnabled}
          onPause={togglePause}
          onLive={returnToLive}
          onExplore={toggleExplore}
          onSound={() => void toggleSound()}
          onShare={() => void shareCurrent()}
        />

        <p className="view-caption" aria-hidden="true">
          <span className="view-caption__follow">{isLiveView ? "FOLLOWING LIVE" : viewMode === "overview" ? "OVERVIEW" : stream.isPaused ? "DISPLAY PAUSED" : "EXPLORING"}&nbsp; • &nbsp;{termRate}</span>
          <span className="view-caption__legend">
            <span className="view-caption__add">+ ABOVE</span>
            <span className="view-caption__separator">&nbsp; · &nbsp;</span>
            <span className="view-caption__subtract">− BELOW</span>
          </span>
          <span className="view-caption__scale">10⁶&nbsp; • &nbsp;{termRate}</span>
        </p>

        <HistorySheet
          stage={sheetStage}
          terms={stream.recentTerms}
          liveTerm={stream.liveTerm}
          liveTerms={stream.liveTerms}
          narrationEnabled={narrationEnabled}
          narrationRate={narrationRate}
          soundEnabled={soundEnabled}
          onStageChange={setSheetStage}
          onPanLeft={() => {
            visualizationRef.current?.panLeft();
            setViewMode("explore");
          }}
          onPanRight={() => {
            visualizationRef.current?.panRight();
            setViewMode("explore");
          }}
          onPanUp={() => {
            visualizationRef.current?.panUp();
            setViewMode("explore");
          }}
          onPanDown={() => {
            visualizationRef.current?.panDown();
            setViewMode("explore");
          }}
          onZoomIn={() => {
            visualizationRef.current?.zoomIn();
            setViewMode("explore");
          }}
          onZoomOut={() => {
            visualizationRef.current?.zoomOut();
            setViewMode("explore");
          }}
          onCenter={() => {
            visualizationRef.current?.center();
            setViewMode("follow");
          }}
          onNarrationToggle={toggleNarration}
          onNarrationRateChange={setNarrationRate}
          onSound={() => void toggleSound()}
          onShare={() => void shareCurrent()}
        />

        <MobileDock
          isPaused={stream.isPaused}
          historyOpen={sheetStage > 0}
          onPause={togglePause}
          onOverview={showOverview}
          onHistory={() => setSheetStage((stage) => stage === 0 ? 1 : 0)}
        />

        <p className="sync-indicator">
          {stream.world.connection === "live" ? "SYNCED" : stream.world.connection.toUpperCase()}&nbsp;&nbsp; {syncTime} UTC
        </p>
        <span id="pause-help" className="sr-only">
          一時停止中も世界共通の数列は進行し、再開時に現在へ戻ります。
        </span>
        <p id="ui-status" className={uiStatus ? "ui-status" : "sr-only"} role="status" aria-atomic="true">{uiStatus}</p>
        <p id="term-announcer" className="sr-only" role="status" aria-live="polite" aria-atomic="true">{announcerText}</p>
        <p className="sr-only" aria-hidden="true">現在の移動は {moveSymbol} {formatDecimal(stream.displayTerm.step)}</p>
      </main>
    </div>
  );
}
